import React, { useState, useEffect } from "react";

import { Combobox } from "@headlessui/react";
import {
  ChevronUpDownIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/20/solid";
import { ReactComponent as SearchIcon } from "../../assets/images/search.svg";
import classNames from "../../utils/classnames";

export default function TokenFunctionSelect({
  options,
  handleSelect,
  hideSearch = false,
  addPropertyLink,
  labelStyles,
  descriptionStyles,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (selectedOption) {
      handleSelect(selectedOption);
    }
  }, [selectedOption]);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          const q = query.toLowerCase();
          const labelLowerCase = option.label.toLowerCase();

          return labelLowerCase.includes(q) || option.value.includes(q);
        });

  const optionsLimit = 200;
  const moreOptions = filteredOptions.length > optionsLimit;
  const visibleOptions = filteredOptions.slice(0, optionsLimit);

  return (
    <Combobox
      value={selectedOption}
      as="div"
      className="absolute min-w-[350px] top-0 left-full pl-2  bg-transparent min-h-[100px]"
      onChange={(option) => {
        setSelectedOption(option);
      }}
    >
      <div className="relative">
        {!hideSearch && (
          <div className="relative px-3 pt-3 pb-3 bg-white border-t border-l border-r rounded-t-lg border-tone-500">
            <SearchIcon className="absolute -translate-y-1/2 top-1/2 left-6" />
            <Combobox.Input
              onKeyDown={(e) => e.stopPropagation()}
              className=" block w-full rounded-md bg-white py-1.5 pl-8 pr-12 "
              onChange={(event) => setQuery(event.target.value)}
              value={query}
              onBlur={() => setQuery("")}
              placeholder="Find..."
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
              <ChevronUpDownIcon
                className="text-gray-400 size-5"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
        )}

        {addPropertyLink && (
          <div className="relative w-full px-3 -mt-3 text-right bg-white border-l border-r border-tone-500">
            <a
              href={addPropertyLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                window.open(addPropertyLink, "_blank", "noopener,noreferrer");
              }}
            >
              Create new property
              <ArrowTopRightOnSquareIcon className="inline-block w-4 h-4 ml-1 align-middle" />
            </a>
          </div>
        )}

        <Combobox.Options
          static
          className={classNames(
            hideSearch ? "rounded-t-lg border-t py-3" : "",
            "border-tone-500 rounded-b-lg border-b border-l border-r absolute z-10 max-h-60 w-full overflow-auto bg-white text-base focus:outline-none sm:text-sm",
          )}
        >
          {visibleOptions.map((option) => (
            <Combobox.Option
              key={option.value}
              value={option}
              className="relative px-3 py-2 mx-3 rounded-lg select-none hover:bg-tone-100"
            >
              <div className="flex flex-col items-baseline w-full gap-1 ">
                <span className={classNames(labelStyles)}>{option.label}</span>
                <span
                  className={classNames(
                    descriptionStyles,
                    "text-xs text-tone-700",
                  )}
                >
                  {option.description}
                  {option.label !== option.value && option.value}
                </span>
              </div>
            </Combobox.Option>
          ))}
          {moreOptions && (
            <div className="px-3 py-2 text-sm bg-white border-t border-tone-200">
              Too many results. Please refine your search.
            </div>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
